import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { L2NavItem, L2Tint } from "./L2Nav";

/** Configuration the L2 rail at app-shell level reads from context.
 *  Each L1 section (Today, Explore, Engagements, …) registers its own
 *  config when its route mounts; the shell picks it up and renders the
 *  vertical rail in the second column next to the sidebar. */
export interface L2NavConfig {
  items: ReadonlyArray<L2NavItem>;
  activeId: string | null;
  onPick: (id: string) => void;
  railAriaLabel: string;
  tint?: L2Tint;
  /** Optional column header above the rail (e.g. "Explore"). */
  title?: string;
  /** Optional trailer (e.g. Settings' Modes section). */
  railTrailer?: ReactNode;
}

interface L2NavContextValue {
  config: L2NavConfig | null;
  setConfig: (config: L2NavConfig | null) => void;
}

const L2NavContext = createContext<L2NavContextValue>({
  config: null,
  setConfig: () => {},
});

export function L2NavProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<L2NavConfig | null>(null);
  return (
    <L2NavContext.Provider value={{ config, setConfig }}>
      {children}
    </L2NavContext.Provider>
  );
}

export function useL2Nav(): L2NavContextValue {
  return useContext(L2NavContext);
}

/** Hook a page calls to register its L2 rail with the app shell. The
 *  shell mounts the rail next to the L1 sidebar; the page itself just
 *  renders its content column. Re-registration on dep change keeps the
 *  rail responsive to URL-driven activeId changes. Unmount clears so
 *  the next route lands clean. */
export function useRegisterL2(config: L2NavConfig): void {
  const ctx = useContext(L2NavContext);
  useEffect(() => {
    ctx.setConfig(config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.items,
    config.activeId,
    config.onPick,
    config.railAriaLabel,
    config.tint,
    config.title,
    config.railTrailer,
  ]);
  useEffect(() => {
    return () => ctx.setConfig(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
